import { useMemo } from 'react';

import { usePrevious } from '@garnish/core';
import { type RequestPolicy } from 'urql';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLoyaltyPremiumStatusQuery } from './GraphQL/LoyaltyPremiumStatus.generated';

/**
 * Returns the loyalty premium status for this customer.
 * Should be called with `cache-and-network` on app load.
 * Should be called without params or with `cache-only` everywhere else.
 */
export const useLoyaltyPremiumStatus = (params: UseLoyaltyPremiumStatusParams = {}) => {
  const { requestPolicy = 'cache-only' } = params;

  const isLoggedIn = useIsLoggedIn();

  const [response] = useLoyaltyPremiumStatusQuery({
    requestPolicy,
    pause: !isLoggedIn,
  });

  const isFetchingPremiumStatus = response.fetching;
  const isPremiumStatusStale = response.stale;

  const prevIsPremiumStatusStale = usePrevious(isPremiumStatusStale);
  const prevIsFetchingPremiumStatus = usePrevious(isFetchingPremiumStatus);

  // This flag indicates that the latest date has been resolved.
  // Useful for sending dynamic events based on the current state.
  const hasResolvedPremiumStatus =
    (prevIsPremiumStatusStale && !isPremiumStatusStale) ||
    (prevIsFetchingPremiumStatus && !isFetchingPremiumStatus);

  const status = response.data?.loyaltyProfile?.rewardsPremiumStatus;

  const isPremium = Boolean(status?.isActive);
  const premiumExpiration = isPremium ? status?.expirationDate : undefined;

  return useMemo(
    () => ({
      isFetchingPremiumStatus,
      hasResolvedPremiumStatus,
      isPremium,
      premiumExpiration,
    }),
    [hasResolvedPremiumStatus, isFetchingPremiumStatus, isPremium, premiumExpiration],
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type UseLoyaltyPremiumStatusParams = {
  requestPolicy?: RequestPolicy;
};
